import React, { useState, useContext, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { ThemeContext } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';

// Notification Toast Components
const NotificationToast = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  min-width: 300px;
  background-color: ${props => props.$type === 'success' ? '#2ecc71' : '#e74c3c'};
  color: white;
  padding: 16px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 9999;
  animation: slideIn 0.3s ease-out forwards;
  @keyframes slideIn {
    from { transform: translateX(100%); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }
`;

const NotificationMessage = styled.div`
  flex: 1;
  font-weight: 500;
`;

const CloseNotificationButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  margin-left: 10px;
  opacity: 0.8;
  transition: opacity 0.2s;
  &:hover { opacity: 1; }
`;

const LaporKTSPage = () => {
  // --- ROLE DETECTION based on class_type ---
  const userData = JSON.parse(localStorage.getItem('user') || '{}');
  const classTypes = Array.isArray(userData.class_type) ? userData.class_type : (userData.class_type ? [userData.class_type] : []);

  const isGuruBengkel = classTypes.includes('praktek');
  const isGuruTeori = classTypes.includes('teori');
  const isGuruBk = classTypes.includes('bk');
  const isMultiRole = isGuruBengkel && isGuruTeori;

  const { darkMode } = useContext(ThemeContext);
  const navigate = useNavigate();
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showSubSuggestions, setShowSubSuggestions] = useState(false);
  const [showStudentSuggestions, setShowStudentSuggestions] = useState(false);
  const [showTeoriStudentSuggestions, setShowTeoriStudentSuggestions] = useState(false);
  const [showBkStudentSuggestions, setShowBkStudentSuggestions] = useState(false);
  const [showJenisKetidaksesuaianSuggestions, setShowJenisKetidaksesuaianSuggestions] = useState(false);
  const [showTeoriJenisKetidaksesuaianSuggestions, setShowTeoriJenisKetidaksesuaianSuggestions] = useState(false);
  const [showBkJenisKetidaksesuaianSuggestions, setShowBkJenisKetidaksesuaianSuggestions] = useState(false);
  const suggestionRef = useRef(null);
  const subSuggestionRef = useRef(null);
  const studentSuggestionRef = useRef(null);
  const teoriStudentSuggestionRef = useRef(null);
  const bkStudentSuggestionRef = useRef(null);
  
  // API Data States
  const [studentOptions, setStudentOptions] = useState([]);
  const [ktsOptions, setKtsOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // State for multi-role form switching
  const [formMode, setFormMode] = useState(isGuruBengkel ? 'bengkel' : 'teori');

  // Get auth token
  const getAuthToken = () => {
    return localStorage.getItem('token'); 
  };

  // API base URL
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/';

  // Fetch students from API
  const fetchStudents = async () => {
    try {
      const token = getAuthToken();
      const userRole = localStorage.getItem('userRole');
      const endpoint = userRole === 'admin' ? '/api/admin/kts-students' : '/api/kts-students';
      const response = await fetch(`${API_BASE_URL.replace(/\/$/, '')}${endpoint}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setStudentOptions(data.data);
        }
      }
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  // Fetch KTS options from API
  const fetchKtsOptions = async (type) => {
    if (!type) return; // Do not fetch if type is not specified

    try {
      const token = getAuthToken();
      const userRole = localStorage.getItem('userRole');
      const endpoint = userRole === 'admin' ? '/api/admin/kts-options' : '/api/kts-options';
      const response = await fetch(`${API_BASE_URL.replace(/\/$/, '')}${endpoint}?type=${type}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setKtsOptions(data.data);
        } else {
          console.error('Failed to fetch KTS options:', data.message);
        }
      } else {
        console.error(`Error fetching KTS options: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error fetching KTS options:', error);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchStudents();
  }, []);

  // Fetch KTS options based on role and form mode, re-fetching when mode changes for multi-role users
  useEffect(() => {
    let typeToFetch;

    if (isMultiRole) {
      // For multi-role users, the type depends on the currently selected form
      typeToFetch = formMode === 'bengkel' ? 'praktek' : 'teori';
    } else {
      // For single-role users, the type is fixed
      if (isGuruBengkel) typeToFetch = 'praktek';
      else if (isGuruTeori) typeToFetch = 'teori';
      else if (isGuruBk) typeToFetch = 'bk';
    }

    if (typeToFetch) {
      fetchKtsOptions(typeToFetch);
    }
  }, [formMode, isMultiRole, isGuruBengkel, isGuruTeori, isGuruBk]);

  // Get filtered KTS options based on search query
  const getFilteredKtsOptions = (searchQuery) => {
    if (!searchQuery) return ktsOptions;
    return ktsOptions.filter(option => 
      option.description.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  // Get pelanggaran details by ID for auto-populating fields
  const getPelanggaranById = (pelanggaranId) => {
    return ktsOptions.find(option => option.id === parseInt(pelanggaranId));
  };

  // Auto-populate jam and section based on selected pelanggaran
  const handlePelanggaranSelection = (pelanggaran, formType) => {
    if (!pelanggaran) return;

    // Determine jam value based on type
    const jamValue = pelanggaran.jam_kompensasi;
    const sectionValue = pelanggaran.section;
    const jamKersosValue = pelanggaran.jam_kersos || '';
    const poinSikapValue = pelanggaran.poin_sikap || '';
    const konsekuensiTambahanValue = pelanggaran.konsekuensi_tambahan || '';

    // Update appropriate form state
    if (formType === 'kompensasi') {
      setKompensasiData(prev => ({
        ...prev,
        jenisKetidaksesuaian: pelanggaran.description,
        jenisKetidaksesuaianSearch: pelanggaran.description,
        kts_id: pelanggaran.id,
        total_hour: jamValue.toString(),
        total_hour_kersos: jamKersosValue.toString(),
        appreciation_point: poinSikapValue.toString(),
        additional_consequence: konsekuensiTambahanValue,
        section: sectionValue
      }));
    } else if (formType === 'teori') {
      setTeoriData(prev => ({
        ...prev,
        jenisKetidaksesuaian: pelanggaran.description,
        jenisKetidaksesuaianSearch: pelanggaran.description,
        kts_id: pelanggaran.id,
        total_hour: jamValue.toString(),
        total_hour_kersos: jamKersosValue.toString(),
        appreciation_point: poinSikapValue.toString(),
        additional_consequence: konsekuensiTambahanValue,
        section: sectionValue,
      }));
    } else if (formType === 'bk') {
      setBkData(prev => ({
        ...prev,
        jenisKetidaksesuaian: pelanggaran.description,
        jenisKetidaksesuaianSearch: pelanggaran.description,
        kts_id: pelanggaran.id,
        total_hour: jamValue.toString(),
        total_hour_kersos: jamKersosValue.toString(),
        appreciation_point: poinSikapValue.toString(),
        additional_consequence: konsekuensiTambahanValue,
        section: sectionValue,
      }));
    }
  };

  // --- Per Tab State ---
  const [activeTab, setActiveTab] = useState('kompensasi'); // For Guru Bengkel

  // Kompensasi Form State (Bengkel)
  const [kompensasiData, setKompensasiData] = useState({
    category: 'kompensasi',
    selectedStudents: [],
    studentSearch: '',
    jenisKetidaksesuaian: '',
    jenisKetidaksesuaianSearch: '', // For auto-suggestion
    kts_id: '',
    total_hour: '',
    total_hour_kersos: '',
    appreciation_point: '',
    additional_consequence: '',
    section: '',
    shift: '',
    description: ''
  });

  // Ketidakhadiran Form State (Bengkel)
  const [ketidakhadiranData, setKetidakhadiranData] = useState({
    category: 'ketidakhadiran',
    selectedStudents: [],
    studentSearch: '',
    jenisKetidakhadiran: '',
    absent_type: '',
    total_hour: '',
    total_hour_kersos: '',
    appreciation_point: '',
    section: '',
    shift: '',
    description: ''
  });

  // Guru Teori Form State (simplified)
  const [teoriData, setTeoriData] = useState({
    category: 'kompensasi', // Default category for teori
    selectedStudents: [],
    studentSearch: '',
    jenisKetidaksesuaian: '',
    jenisKetidaksesuaianSearch: '', // For auto-suggestion
    kts_id: '',
    total_hour: '',
    total_hour_kersos: '',
    appreciation_point: '',
    additional_consequence: '',
    section: '',
    description: ''
  });

  // Teori Ketidakhadiran Form State
  const [teoriKetidakhadiranData, setTeoriKetidakhadiranData] = useState({
    category: 'ketidakhadiran',
    selectedStudents: [],
    studentSearch: '',
    jenisKetidakhadiran: '',
    absent_type: '',
    total_hour: '',
    total_hour_kersos: '',
    appreciation_point: '',
    section: '-',
    description: ''
  });

  // Guru BK Form State (simplified)
  const [bkData, setBkData] = useState({
    category: 'kompensasi', // Default category for BK
    selectedStudents: [],
    studentSearch: '',
    jenisKetidaksesuaian: '',
    jenisKetidaksesuaianSearch: '', // For auto-suggestion
    kts_id: '',
    total_hour: '',
    total_hour_kersos: '',
    appreciation_point: '',
    additional_consequence: '',
    section: '',
    description: ''
  });

  // Notification state
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

  // Auto-hide notification after 5 seconds
  useEffect(() => {
    if (notification.show) {
      const timer = setTimeout(() => {
        setNotification({ show: false, message: '', type: 'success' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification.show]);

  // Submit form data to API
  const submitKtsReport = async (formData, submittedClassType) => {
    try {
      setSubmitting(true);
      const token = getAuthToken();
      
      const payload = {
        class_type: submittedClassType,
        category: formData.category,
        student_ids: formData.selectedStudents,
        description: formData.description,
        ...(formData.kts_id && { kts_id: formData.kts_id }),
        ...(formData.absent_type && { absent_type: formData.absent_type }),
        ...(formData.total_hour && { total_hour: parseFloat(formData.total_hour) }),
        ...(formData.total_hour_kersos && { total_hour_kersos: parseFloat(formData.total_hour_kersos) }),
        ...(formData.appreciation_point && { appreciation_point: parseFloat(formData.appreciation_point) }),
        ...(formData.additional_consequence && { additional_consequence: formData.additional_consequence }),
        ...(formData.section && { section: formData.section }),
        ...(formData.shift && { shift: formData.shift })
      };

      const response = await fetch(`${API_BASE_URL.replace(/\/$/, '')}/api/kts-reports`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setNotification({ show: true, message: 'Laporan KTS berhasil disimpan!', type: 'success' });
        // Reset form based on teacher type
        if (isGuruBengkel) {
          if (activeTab === 'kompensasi') {
            setKompensasiData({
              category: 'kompensasi',
              selectedStudents: [],
              studentSearch: '',
              jenisKetidaksesuaian: '',
              jenisKetidaksesuaianSearch: '',
              kts_id: '',
              total_hour: '',
              total_hour_kersos: '',
              appreciation_point: '',
              additional_consequence: '',
              section: '',
              shift: '',
              description: ''
            });
          } else {
            setKetidakhadiranData({
              category: 'ketidakhadiran',
              selectedStudents: [],
              studentSearch: '',
              jenisKetidakhadiran: '',
              absent_type: '',
              total_hour: '',
              total_hour_kersos: '',
              appreciation_point: '',
              section: '',
              shift: '',
              description: ''
            });
          }
        } else if (isGuruTeori) {
          if (formData.category === 'kompensasi') {
            setTeoriData({
              category: 'kompensasi',
              selectedStudents: [],
              studentSearch: '',
              jenisKetidaksesuaian: '',
              jenisKetidaksesuaianSearch: '',
              kts_id: '',
              total_hour: '',
              total_hour_kersos: '',
              appreciation_point: '',
              additional_consequence: '',
              section: '',
              description: ''
            });
          } else {
            setTeoriKetidakhadiranData({
              category: 'ketidakhadiran',
              selectedStudents: [],
              studentSearch: '',
              jenisKetidakhadiran: '',
              absent_type: '',
              total_hour: '',
              total_hour_kersos: '',
              appreciation_point: '',
              section: '',
              description: ''
            });
          }
        } else if (isGuruBk) {
          setBkData({
            category: 'kompensasi',
            selectedStudents: [],
            studentSearch: '',
            jenisKetidaksesuaian: '',
            jenisKetidaksesuaianSearch: '',
            kts_id: '',
            total_hour: '',
            total_hour_kersos: '',
            appreciation_point: '',
            additional_consequence: '',
            section: '',
            description: ''
          });
        }
      } else {
        setNotification({ show: true, message: `Error: ${data.message || 'Gagal menyimpan laporan'}`, type: 'error' });
      }
    } catch (error) {
      console.error('Error submitting KTS report:', error);
      setNotification({ show: true, message: 'Terjadi kesalahan saat menyimpan laporan', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle form submissions
  const handleKompensasiSubmit = () => {
    if (!kompensasiData.kts_id || kompensasiData.selectedStudents.length === 0 || !kompensasiData.description) {
      setNotification({ show: true, message: 'Mohon lengkapi semua field yang diperlukan', type: 'error' });
      return;
    }
    submitKtsReport(kompensasiData, 'praktek');
  };

  const handleKetidakhadiranSubmit = () => {
    if (ketidakhadiranData.selectedStudents.length === 0 || !ketidakhadiranData.jenisKetidakhadiran || !ketidakhadiranData.total_hour || !ketidakhadiranData.section || !ketidakhadiranData.shift || !ketidakhadiranData.description) {
      setNotification({ show: true, message: 'Mohon lengkapi semua field yang diperlukan', type: 'error' });
      return;
    }
    submitKtsReport(ketidakhadiranData, 'praktek');
  };

  const handleTeoriSubmit = () => {
    if (!teoriData.kts_id || teoriData.selectedStudents.length === 0 || !teoriData.description) {
      setNotification({ show: true, message: 'Mohon lengkapi semua field yang diperlukan', type: 'error' });
      return;
    }
    submitKtsReport(teoriData, 'teori');
  };

  const handleTeoriKetidakhadiranSubmit = () => {
    if (teoriKetidakhadiranData.selectedStudents.length === 0 || !teoriKetidakhadiranData.jenisKetidakhadiran || !teoriKetidakhadiranData.total_hour || !teoriKetidakhadiranData.section || !teoriKetidakhadiranData.description) {
      setNotification({ show: true, message: 'Mohon lengkapi semua field yang diperlukan', type: 'error' });
      return;
    }
    submitKtsReport(teoriKetidakhadiranData, 'teori');
  };

  const handleBkSubmit = () => {
    if (!bkData.kts_id || bkData.selectedStudents.length === 0 || !bkData.description) {
      setNotification({ show: true, message: 'Mohon lengkapi semua field yang diperlukan', type: 'error' });
      return;
    }
    submitKtsReport(bkData, 'teori');
  };

  // Helper function to add student to selected list
  const addStudentToList = (student, formType) => {
    const studentId = student.id;
    
    if (formType === 'kompensasi') {
      if (!kompensasiData.selectedStudents.includes(studentId)) {
        setKompensasiData({
          ...kompensasiData,
          selectedStudents: [...kompensasiData.selectedStudents, studentId],
          studentSearch: ''
        });
      }
    } else if (formType === 'ketidakhadiran') {
      if (!ketidakhadiranData.selectedStudents.includes(studentId)) {
        setKetidakhadiranData({
          ...ketidakhadiranData,
          selectedStudents: [...ketidakhadiranData.selectedStudents, studentId],
          studentSearch: ''
        });
      }
    } else if (formType === 'teori') {
      if (activeTab === 'kompensasi') {
        if (!teoriData.selectedStudents.includes(studentId)) {
          setTeoriData({
            ...teoriData,
            selectedStudents: [...teoriData.selectedStudents, studentId],
            studentSearch: ''
          });
        }
      } else { // ketidakhadiran
        if (!teoriKetidakhadiranData.selectedStudents.includes(studentId)) {
          setTeoriKetidakhadiranData({
            ...teoriKetidakhadiranData,
            selectedStudents: [...teoriKetidakhadiranData.selectedStudents, studentId],
            studentSearch: ''
          });
        }
      }
    } else if (formType === 'bk') {
      if (!bkData.selectedStudents.includes(studentId)) {
        setBkData({
          ...bkData,
          selectedStudents: [...bkData.selectedStudents, studentId],
          studentSearch: ''
        });
      }
    }
    
    // Hide suggestions
    setShowStudentSuggestions(false);
    setShowTeoriStudentSuggestions(false);
    setShowBkStudentSuggestions(false);
  };

  // Helper function to remove student from selected list
  const removeStudentFromList = (studentId, formType) => {
    if (formType === 'kompensasi') {
      setKompensasiData({
        ...kompensasiData,
        selectedStudents: kompensasiData.selectedStudents.filter(id => id !== studentId)
      });
    } else if (formType === 'ketidakhadiran') {
      setKetidakhadiranData({
        ...ketidakhadiranData,
        selectedStudents: ketidakhadiranData.selectedStudents.filter(id => id !== studentId)
      });
    } else if (formType === 'teori') {
      if (activeTab === 'kompensasi') {
        setTeoriData({
          ...teoriData,
          selectedStudents: teoriData.selectedStudents.filter(id => id !== studentId)
        });
      } else {
        setTeoriKetidakhadiranData({
          ...teoriKetidakhadiranData,
          selectedStudents: teoriKetidakhadiranData.selectedStudents.filter(id => id !== studentId)
        });
      }
    } else if (formType === 'bk') {
      setBkData({
        ...bkData,
        selectedStudents: bkData.selectedStudents.filter(id => id !== studentId)
      });
    }
  };

  // Get student name by ID
  const getStudentNameById = (studentId) => {
    const student = studentOptions.find(s => s.id === studentId);
    return student ? `${student.name} (${student.class})` : 'Unknown Student';
  };

  // --- Helper: Parse class grade from selected student(s) ---
  // Calculates class grade (X, XI, XII) using the first selected student.
  // Robust parsing handles formats like "NAME (XTP6)", "XII TKR 3", "XI-TP6".
  // Fallback: returns null when data is missing or malformed.
  const inferClassGradeFromSelected = (selectedStudents) => {
    if (!Array.isArray(selectedStudents) || selectedStudents.length === 0) return null;
    const firstId = selectedStudents[0];
    const student = studentOptions.find(s => s.id === firstId);
    const raw = (student?.class || getStudentNameById(firstId) || '').toString();
    const s = raw.toUpperCase();
    if (s.includes('XII')) return 'XII';
    if (s.includes('XI')) return 'XI';
    // Detect grade X only when XI/XII are not present
    if (!s.includes('XI') && !s.includes('XII') && s.includes('X')) return 'X';
    return null;
  };

  // --- Helper: Compute practical absence total hours ---
  // For absence type Sakit/Ijin in Form Praktek (ketidakhadiran):
  // - XI/XII → -7 hours
  // - X      → -5 hours
  // Fallback to -7 when class cannot be determined to preserve prior behavior.
  const computePraktekTotalHourForAbsence = (absentType, selectedStudents) => {
    const t = (absentType || '').toLowerCase();
    if (t !== 'sakit' && t !== 'ijin') return '';
    const grade = inferClassGradeFromSelected(selectedStudents);
    if (grade === 'X') return '-5';
    if (grade === 'XI' || grade === 'XII') return '-7';
    return '-7';
  };

  // --- RETURN: Render Forms Based on Role ---
  return (
    <PageContainer>
      {notification.show && (
        <NotificationToast $type={notification.type}>
          <NotificationMessage>{notification.message}</NotificationMessage>
          <CloseNotificationButton onClick={() => setNotification({ show: false, message: '', type: 'success' })}>×</CloseNotificationButton>
        </NotificationToast>
      )}
      <PageHeader>
        <h1>Lapor Ketidaksesuaian</h1>
        <p>{isGuruTeori ? 'Kelola laporan ketidaksesuaian teori siswa' : isGuruBk ? 'Kelola laporan ketidaksesuaian BK siswa' : 'Kelola laporan ketidaksesuaian siswa'}</p>
      </PageHeader>
      <FormContainer>
        <FormWrapper theme={darkMode ? 'dark' : 'light'}>
          <FormHeader>
            <HeaderContent>
              <h2>{
                isMultiRole 
                  ? 'Laporan Ketidaksesuaian' 
                  : isGuruTeori 
                    ? 'Laporan Ketidaksesuaian (Teori)' 
                    : isGuruBk 
                      ? 'Laporan Ketidaksesuaian (BK)' 
                      : 'Formulir Laporan Ketidaksesuaian (Bengkel)'
              }</h2>
            </HeaderContent>
          </FormHeader>
          <FormContent>
            {/* ROLE SWITCHER FOR MULTI-ROLE TEACHERS (Bengkel and Teori) */}
            {isMultiRole && (
              <TypeToggleContainer>
                <TypeToggleButtonGroup>
                  <TypeToggleButton active={formMode === 'bengkel'} onClick={() => setFormMode('bengkel')}>
                    FORM PRAKTEK
                  </TypeToggleButton>
                  <TypeToggleButton active={formMode === 'teori'} onClick={() => setFormMode('teori')}>
                    FORM TEORI
                  </TypeToggleButton>
                </TypeToggleButtonGroup>
              </TypeToggleContainer>
            )}

            {/* Case 1: Bengkel teacher OR Multi-role teacher in 'bengkel' mode */}
            {((isGuruBengkel && !isGuruTeori) || (isMultiRole && formMode === 'bengkel')) && (
              <>
                <TypeToggleContainer>
                  <TypeToggleButtonGroup>
                    <TypeToggleButton active={activeTab === 'kompensasi'} onClick={() => setActiveTab('kompensasi')}>KOMPENSASI</TypeToggleButton>
                    <TypeToggleButton active={activeTab === 'ketidakhadiran'} onClick={() => setActiveTab('ketidakhadiran')}>KETIDAKHADIRAN</TypeToggleButton>
                  </TypeToggleButtonGroup>
                </TypeToggleContainer>
                {activeTab === 'kompensasi' && (
                  <form onSubmit={e => { e.preventDefault(); handleKompensasiSubmit(); }}>
                    {/* Kompensasi Form Fields */}
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Nama Siswa</label>
                      <input
                        type="text"
                        placeholder="Ketik & cari nama siswa..."
                        value={kompensasiData.studentSearch || ''}
                        onChange={e => {
                          const value = e.target.value;
                          setKompensasiData({ ...kompensasiData, studentSearch: value });
                          setShowStudentSuggestions(true);
                        }}
                        onFocus={() => setShowStudentSuggestions(true)}
                        style={{ marginBottom: 8 }}
                      />
                      {showStudentSuggestions && kompensasiData.studentSearch && (
                        <SuggestionsList theme={darkMode ? 'dark' : 'light'}>
                          {studentOptions.filter(opt =>
                            opt.name.toLowerCase().includes(kompensasiData.studentSearch.toLowerCase()) &&
                            !kompensasiData.selectedStudents.includes(opt.id)
                          ).map(opt => (
                            <StudentSuggestionItem
                              key={opt.id}
                              theme={darkMode ? 'dark' : 'light'}
                              onClick={() => {
                                addStudentToList(opt, 'kompensasi');
                              }}
                            >
                              <span className="student-name">{opt.name}</span>{' '}
                              <span className="student-info">({opt.class})</span>
                            </StudentSuggestionItem>
                          ))}
                        </SuggestionsList>
                      )}
                      <SelectedStudentsContainer>
                        {kompensasiData.selectedStudents.map((studentId, idx) => (
                          <SelectedStudentTag key={studentId}>
                            <span style={{ fontWeight: 500 }}>{getStudentNameById(studentId)}</span>
                            <button type="button" onClick={() => {
                              removeStudentFromList(studentId, 'kompensasi');
                            }}>×</button>
                          </SelectedStudentTag>
                        ))}
                      </SelectedStudentsContainer>
                    </FormGroup>
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Jenis Ketidaksesuaian</label>
                      <input
                        type="text"
                        placeholder="Ketik & cari jenis ketidaksesuaian..."
                        value={kompensasiData.jenisKetidaksesuaianSearch || ''}
                        onChange={e => {
                          const value = e.target.value;
                          setKompensasiData({ 
                            ...kompensasiData, 
                            jenisKetidaksesuaianSearch: value,
                            jenisKetidaksesuaian: value,
                            kts_id: '', // Reset selection when typing
                            total_hour: '',
                            total_hour_kersos: '',
                            appreciation_point: '',
                            additional_consequence: '',
                            section: ''
                          });
                          setShowJenisKetidaksesuaianSuggestions(true);
                        }}
                        onFocus={() => setShowJenisKetidaksesuaianSuggestions(true)}
                        required
                      />
                      {showJenisKetidaksesuaianSuggestions && kompensasiData.jenisKetidaksesuaianSearch && (
                        <SuggestionsList theme={darkMode ? 'dark' : 'light'}>
                          {getFilteredKtsOptions(kompensasiData.jenisKetidaksesuaianSearch).map((option, index) => (
                            <SuggestionItem
                              key={index}
                              theme={darkMode ? 'dark' : 'light'}
                              onClick={() => {
                                handlePelanggaranSelection(option, 'kompensasi');
                                setShowJenisKetidaksesuaianSuggestions(false);
                              }}
                            >
                              {option.description}
                            </SuggestionItem>
                          ))}
                        </SuggestionsList>
                      )}
                    </FormGroup>
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Section</label>
                      <input 
                        type="text" 
                        value={kompensasiData.section} 
                        readOnly
                        style={{ backgroundColor: darkMode ? '#2a2a2a' : '#f5f5f5', cursor: 'not-allowed' }}
                        placeholder="Otomatis terisi berdasarkan jenis ketidaksesuaian"
                      />
                    </FormGroup>
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Jumlah Jam Lembur</label>
                      <input 
                        type="number" 
                        step="0.1"
                        value={kompensasiData.total_hour} 
                       
                       
                        placeholder="Otomatis terisi berdasarkan jenis ketidaksesuaian"
                      />
                    </FormGroup>
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Jumlah Jam Kersos</label>
                      <input 
                        type="number" 
                        step="0.1"
                        value={kompensasiData.total_hour_kersos} 
                       
                      
                        placeholder="Otomatis terisi berdasarkan jenis ketidaksesuaian"
                      />
                    </FormGroup>
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Poin Sikap</label>
                      <input 
                        type="number" 
                        step="0.1"
                        value={kompensasiData.appreciation_point} 
                       
                        placeholder="Otomatis terisi berdasarkan jenis ketidaksesuaian"
                      />
                    </FormGroup>
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Konsekuensi Tambahan</label>
                      <textarea 
                        value={kompensasiData.additional_consequence} 
                        readOnly
                        style={{ backgroundColor: darkMode ? '#2a2a2a' : '#f5f5f5', cursor: 'not-allowed' }}
                        placeholder="Otomatis terisi berdasarkan jenis ketidaksesuaian"
                      />
                    </FormGroup>
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Shift</label>
                      <select value={kompensasiData.shift} onChange={e => setKompensasiData({ ...kompensasiData, shift: e.target.value })} required>
                        <option value="">Pilih Shift</option>
                        <option value="Shift -">Shift -</option>
                        <option value="Shift 1">Shift 1: 07.00 – 14.30</option>
                        <option value="Shift 2">Shift 2: 14.30 – 22.00</option>
                      </select>
                    </FormGroup>
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Keterangan</label>
                      <textarea value={kompensasiData.description} onChange={e => setKompensasiData({ ...kompensasiData, description: e.target.value })} required />
                    </FormGroup>
                    <FormActions>
                      <SubmitButton type="submit" disabled={submitting}>
                        {submitting ? 'Mengirim...' : 'Kirim Laporan Ketidaksesuaian'}
                      </SubmitButton>
                    </FormActions>
                  </form>
                )}
                {activeTab === 'ketidakhadiran' && (
                  <form onSubmit={e => { e.preventDefault(); handleKetidakhadiranSubmit(); }}>
                    {/* Ketidakhadiran Form Fields */}
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Nama Siswa</label>
                      <input
                        type="text"
                        placeholder="Ketik & cari nama siswa..."
                        value={ketidakhadiranData.studentSearch || ''}
                        onChange={e => {
                          const value = e.target.value;
                          setKetidakhadiranData({ ...ketidakhadiranData, studentSearch: value });
                          setShowStudentSuggestions(true);
                        }}
                        onFocus={() => setShowStudentSuggestions(true)}
                        style={{ marginBottom: 8 }}
                      />
                      {showStudentSuggestions && ketidakhadiranData.studentSearch && (
                        <SuggestionsList theme={darkMode ? 'dark' : 'light'}>
                          {studentOptions.filter(opt =>
                            opt.name.toLowerCase().includes(ketidakhadiranData.studentSearch.toLowerCase()) &&
                            !ketidakhadiranData.selectedStudents.includes(opt.id)
                          ).map(opt => (
                            <StudentSuggestionItem
                              key={opt.id}
                              theme={darkMode ? 'dark' : 'light'}
                              onClick={() => {
                                addStudentToList(opt, 'ketidakhadiran');
                              }}
                            >
                              <span className="student-name">{opt.name}</span>{' '}
                              <span className="student-info">({opt.class})</span>
                            </StudentSuggestionItem>
                          ))}
                        </SuggestionsList>
                      )}
                      <SelectedStudentsContainer>
                        {ketidakhadiranData.selectedStudents.map((studentId, idx) => (
                          <SelectedStudentTag key={studentId}>
                            <span style={{ fontWeight: 500 }}>{getStudentNameById(studentId)}</span>
                            <button type="button" onClick={() => {
                              removeStudentFromList(studentId, 'ketidakhadiran');
                            }}>×</button>
                          </SelectedStudentTag>
                        ))}
                      </SelectedStudentsContainer>
                    </FormGroup>
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Jenis Ketidakhadiran</label>
                      <select
                        id="jenisKetidakhadiran"
                        name="jenisKetidakhadiran"
                        value={ketidakhadiranData.jenisKetidakhadiran}
                        onChange={e => {
                          const value = e.target.value;
                          let newTotalHour = '';
                          let newTotalHourKersos = '';
                          let newAppreciationPoint = '';
                          
                          if (value === 'Sakit') {
                            // Class-based hour: XI/XII → -7, X → -5
                            newTotalHour = computePraktekTotalHourForAbsence('sakit', ketidakhadiranData.selectedStudents);
                            newTotalHourKersos = '0';
                            newAppreciationPoint = '0';
                          } else if (value === 'Ijin') {
                            // Class-based hour: XI/XII → -7, X → -5
                            newTotalHour = computePraktekTotalHourForAbsence('ijin', ketidakhadiranData.selectedStudents);
                            newTotalHourKersos = '0';
                            newAppreciationPoint = '0';
                          } else if (value === 'Alfa') {
                            newTotalHour = '-14';
                            newTotalHourKersos = '-8';
                            newAppreciationPoint = '-40';
                          } else if (value === 'Keterlambatan') {
                            newTotalHour = '0';
                            newTotalHourKersos = '0';
                            newAppreciationPoint = '0';
                          } else if (value === 'Interupsi') {
                            newTotalHour = '0';
                            newTotalHourKersos = '0';
                            newAppreciationPoint = '0';
                          }
                          
                          setKetidakhadiranData({ 
                            ...ketidakhadiranData, 
                            jenisKetidakhadiran: value,
                            absent_type: value.toLowerCase(),
                            total_hour: newTotalHour,
                            total_hour_kersos: newTotalHourKersos,
                            appreciation_point: newAppreciationPoint
                          });
                        }}
                        required
                      >
                        <option value="">Pilih Jenis</option>
                        <option value="Sakit">Sakit</option>
                        <option value="Ijin">Ijin</option>
                        <option value="Alfa">Alfa</option>
                        <option value="Keterlambatan">Keterlambatan</option>
                        <option value="Interupsi">Interupsi</option>
                      </select>
                    </FormGroup>
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Jumlah Jam Lembur (harus minus, contoh: -5)</label>
                      <input 
                        type="number" 
                        step="0.1"
                        value={ketidakhadiranData.total_hour} 
                        onChange={e => setKetidakhadiranData({ ...ketidakhadiranData, total_hour: e.target.value })}
                        placeholder="Contoh: -5"
                        required
                      />
                    </FormGroup>
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Jumlah Jam Kersos</label>
                      <input 
                        type="number" 
                        step="0.1"
                        value={ketidakhadiranData.total_hour_kersos} 
                        
                        
                        placeholder="Otomatis terisi"
                      />
                    </FormGroup>
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Poin Sikap</label>
                      <input
                        type="number"
                        value={ketidakhadiranData.appreciation_point}
                        
                    
                        placeholder="Otomatis terisi"
                      />
                    </FormGroup>
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Section</label>
                      <select 
                        value={ketidakhadiranData.section} 
                        onChange={e => setKetidakhadiranData({ ...ketidakhadiranData, section: e.target.value })}
                        required
                      >
                        <option value="">Pilih Section</option>
                        <option value="LW">LW</option>
                        <option value="Umum">Umum</option>
                        <option value="Mill">Mill</option>
                        <option value="BW">BW</option>
                        <option value="CNC">CNC</option>
                        <option value="CNC/MILL">CNC/MILL</option>
                        <option value="GRIND">GRIND</option>
                      </select>
                    </FormGroup>
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Shift</label>
                      <select value={ketidakhadiranData.shift} onChange={e => setKetidakhadiranData({ ...ketidakhadiranData, shift: e.target.value })} required>
                        <option value="">Pilih Shift</option>
                        <option value="Shift -">Shift -</option>
                        <option value="Shift 1">Shift 1: 07.00 – 14.30</option>
                        <option value="Shift 2">Shift 2: 14.30 – 22.00</option>
                      </select>
                    </FormGroup>
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Keterangan</label>
                      <textarea value={ketidakhadiranData.description} onChange={e => setKetidakhadiranData({ ...ketidakhadiranData, description: e.target.value })} required />
                    </FormGroup>
                    <FormActions>
                      <SubmitButton type="submit" disabled={submitting}>
                        {submitting ? 'Mengirim...' : 'Kirim Laporan Ketidaksesuaian'}
                      </SubmitButton>
                    </FormActions>
                  </form>
                )}
              </>
            )}

            {/* Case 2: Teori teacher OR Multi-role teacher in 'teori' mode */}
            {((isGuruTeori && !isGuruBengkel) || (isMultiRole && formMode === 'teori')) && (
              <>
                <TypeToggleContainer>
                  <TypeToggleButtonGroup>
                    <TypeToggleButton active={activeTab === 'kompensasi'} onClick={() => setActiveTab('kompensasi')}>KOMPENSASI</TypeToggleButton>
                    <TypeToggleButton active={activeTab === 'ketidakhadiran'} onClick={() => setActiveTab('ketidakhadiran')}>KETIDAKHADIRAN</TypeToggleButton>
                  </TypeToggleButtonGroup>
                </TypeToggleContainer>
              
                {activeTab === 'kompensasi' && (
                  <form onSubmit={e => { e.preventDefault(); handleTeoriSubmit(); }}>
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label htmlFor="namaSiswa">Nama Siswa</label>
                      <input
                        type="text"
                        id="namaSiswa"
                        name="namaSiswa"
                        value={teoriData.studentSearch}
                        onChange={e => {
                          const value = e.target.value;
                          setTeoriData({ ...teoriData, studentSearch: value });
                          setShowTeoriStudentSuggestions(true);
                        }}
                        onFocus={() => setShowTeoriStudentSuggestions(true)}
                        placeholder="Masukkan nama siswa"
                      />
                      {showTeoriStudentSuggestions && teoriData.studentSearch && (
                        <SuggestionsList theme={darkMode ? 'dark' : 'light'}>
                          {studentOptions.filter(opt =>
                            opt.name.toLowerCase().includes(teoriData.studentSearch.toLowerCase())
                          ).map(opt => (
                            <StudentSuggestionItem
                              key={opt.id}
                              theme={darkMode ? 'dark' : 'light'}
                              onClick={() => {
                                addStudentToList(opt, 'teori');
                              }}
                            >
                              <span className="student-name">{opt.name}</span>{' '}
                              <span className="student-info">({opt.class})</span>
                            </StudentSuggestionItem>
                          ))}
                        </SuggestionsList>
                      )}
                      <SelectedStudentsContainer>
                        {teoriData.selectedStudents.map((studentId, idx) => (
                          <SelectedStudentTag key={studentId}>
                            <span style={{ fontWeight: 500 }}>{getStudentNameById(studentId)}</span>
                            <button type="button" onClick={() => {
                              removeStudentFromList(studentId, 'teori');
                            }}>×</button>
                          </SelectedStudentTag>
                        ))}
                      </SelectedStudentsContainer>
                    </FormGroup>
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Jenis Ketidaksesuaian</label>
                      <input
                        type="text"
                        placeholder="Ketik & cari jenis ketidaksesuaian..."
                        value={teoriData.jenisKetidaksesuaianSearch || ''}
                        onChange={e => {
                          const value = e.target.value;
                          setTeoriData({ 
                            ...teoriData, 
                            jenisKetidaksesuaianSearch: value,
                            jenisKetidaksesuaian: value,
                            kts_id: '', // Reset selection when typing
                            total_hour: '',
                            total_hour_kersos: '',
                            appreciation_point: '',
                            additional_consequence: '',
                            section: ''
                          });
                          setShowTeoriJenisKetidaksesuaianSuggestions(true);
                        }}
                        onFocus={() => setShowTeoriJenisKetidaksesuaianSuggestions(true)}
                        required
                      />
                      {showTeoriJenisKetidaksesuaianSuggestions && teoriData.jenisKetidaksesuaianSearch && (
                        <SuggestionsList theme={darkMode ? 'dark' : 'light'}>
                          {getFilteredKtsOptions(teoriData.jenisKetidaksesuaianSearch).map((option, index) => (
                            <SuggestionItem
                              key={index}
                              theme={darkMode ? 'dark' : 'light'}
                              onClick={() => {
                                handlePelanggaranSelection(option, 'teori');
                                setShowTeoriJenisKetidaksesuaianSuggestions(false);
                              }}
                            >
                              {option.description}
                            </SuggestionItem>
                          ))}
                        </SuggestionsList>
                      )}
                    </FormGroup>
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Section</label>
                      <input 
                        type="text" 
                        value={teoriData.section} 
                        readOnly
                        style={{ backgroundColor: darkMode ? '#2a2a2a' : '#f5f5f5', cursor: 'not-allowed' }}
                        placeholder="Otomatis terisi berdasarkan jenis ketidaksesuaian"
                      />
                    </FormGroup>
                    <FormGroup theme={darkMode ? 'dark' : 'light'} style={{ display: 'none' }}>
                      <label>Jumlah Jam Lembur</label>
                      <input 
                        type="number" 
                        step="0.1"
                        value={teoriData.total_hour} 
                       
                      
                        placeholder="Otomatis terisi berdasarkan jenis ketidaksesuaian"
                      />
                    </FormGroup>
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Jumlah Jam Kersos</label>
                      <input
                        type="number"
                        step="0.1"
                        value={teoriData.total_hour_kersos}
                        
                       
                        placeholder="Otomatis terisi berdasarkan jenis ketidaksesuaian"
                      />
                    </FormGroup>
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Poin Sikap</label>
                      <input
                        type="number"
                        step="0.1"
                        value={teoriData.appreciation_point}
                        
                       
                        placeholder="Otomatis terisi berdasarkan jenis ketidaksesuaian"
                      />
                    </FormGroup>
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Konsekuensi Tambahan</label>
                      <textarea
                        value={teoriData.additional_consequence}
                        readOnly
                        style={{ backgroundColor: darkMode ? '#2a2a2a' : '#f5f5f5', cursor: 'not-allowed' }}
                        placeholder="Otomatis terisi berdasarkan jenis ketidaksesuaian"
                      />
                    </FormGroup>
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Keterangan</label>
                      <textarea value={teoriData.description} onChange={e => setTeoriData({ ...teoriData, description: e.target.value })} required />
                    </FormGroup>
                    <FormActions>
                      <SubmitButton type="submit" disabled={submitting}>
                        {submitting ? 'Mengirim...' : 'Kirim Laporan Ketidaksesuaian'}
                      </SubmitButton>
                    </FormActions>
                  </form>
                )}
                {activeTab === 'ketidakhadiran' && (
                  <form onSubmit={e => { e.preventDefault(); handleTeoriKetidakhadiranSubmit(); }}>
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Nama Siswa</label>
                      <input
                        type="text"
                        placeholder="Ketik & cari nama siswa..."
                        value={teoriKetidakhadiranData.studentSearch || ''}
                        onChange={e => {
                          const value = e.target.value;
                          setTeoriKetidakhadiranData({ ...teoriKetidakhadiranData, studentSearch: value });
                          setShowTeoriStudentSuggestions(true);
                        }}
                        onFocus={() => setShowTeoriStudentSuggestions(true)}
                        style={{ marginBottom: 8 }}
                      />
                      {showTeoriStudentSuggestions && teoriKetidakhadiranData.studentSearch && (
                        <SuggestionsList theme={darkMode ? 'dark' : 'light'}>
                          {studentOptions.filter(opt =>
                            opt.name.toLowerCase().includes(teoriKetidakhadiranData.studentSearch.toLowerCase()) &&
                            !teoriKetidakhadiranData.selectedStudents.includes(opt.id)
                          ).map(opt => (
                            <StudentSuggestionItem
                              key={opt.id}
                              theme={darkMode ? 'dark' : 'light'}
                              onClick={() => {
                                addStudentToList(opt, 'teori');
                              }}
                            >
                              <span className="student-name">{opt.name}</span>{' '}
                              <span className="student-info">({opt.class})</span>
                            </StudentSuggestionItem>
                          ))}
                        </SuggestionsList>
                      )}
                      <SelectedStudentsContainer>
                        {teoriKetidakhadiranData.selectedStudents.map((studentId, idx) => (
                          <SelectedStudentTag key={studentId}>
                            <span style={{ fontWeight: 500 }}>{getStudentNameById(studentId)}</span>
                            <button type="button" onClick={() => {
                              removeStudentFromList(studentId, 'teori');
                            }}>×</button>
                          </SelectedStudentTag>
                        ))}
                      </SelectedStudentsContainer>
                    </FormGroup>
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Jenis Ketidakhadiran</label>
                      <select
                        value={teoriKetidakhadiranData.jenisKetidakhadiran}
                        onChange={e => {
                          const value = e.target.value;
                          let newTotalHour = '';
                          let newTotalHourKersos = '';
                          let newAppreciationPoint = '';
                          
                          if (value === 'Sakit') {
                            newTotalHour = '0';
                            newTotalHourKersos = '0';
                            newAppreciationPoint = '0';
                          } else if (value === 'Ijin') {
                            newTotalHour = '0';
                            newTotalHourKersos = '0';
                            newAppreciationPoint = '0';
                          } else if (value === 'Alfa') {
                            newTotalHour = '0';
                            newTotalHourKersos = '-8';
                            newAppreciationPoint = '-40';
                          }
                          
                          setTeoriKetidakhadiranData({ 
                            ...teoriKetidakhadiranData, 
                            jenisKetidakhadiran: value,
                            absent_type: value.toLowerCase(),
                            total_hour: newTotalHour,
                            total_hour_kersos: newTotalHourKersos,
                            appreciation_point: newAppreciationPoint
                          });
                        }}
                        required
                      >
                        <option value="">Pilih Jenis</option>
                        <option value="Sakit">Sakit</option>
                        <option value="Ijin">Ijin</option>
                        <option value="Alfa">Alfa</option>
                      </select>
                    </FormGroup>
                    <FormGroup theme={darkMode ? 'dark' : 'light'} style={{ display: 'none' }}>
                      <label>Jumlah Jam Lembur</label>
                      <input 
                        type="number" 
                        step="0.1"
                        value={teoriKetidakhadiranData.total_hour} 
                        onChange={e => setTeoriKetidakhadiranData({ ...teoriKetidakhadiranData, total_hour: e.target.value })}
                        placeholder="Otomatis terisi"
                        required
                       
                      />
                    </FormGroup>
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Jumlah Jam Kersos</label>
                      <input 
                        type="number" 
                        step="0.1"
                        value={teoriKetidakhadiranData.total_hour_kersos} 
                       
                        placeholder="Otomatis terisi"
                      />
                    </FormGroup>
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Poin Sikap</label>
                      <input
                        type="number"
                        value={teoriKetidakhadiranData.appreciation_point}
                       
                        placeholder="Otomatis terisi"
                      />
                    </FormGroup>
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Section</label>
                      <select 
                        value={teoriKetidakhadiranData.section} 
                        onChange={e => setTeoriKetidakhadiranData({ ...teoriKetidakhadiranData, section: e.target.value })}
                        required
                      >
                        <option value="">Pilih Section</option>
                        <option value="-">-</option>
                      </select>
                    </FormGroup>
                    <FormGroup theme={darkMode ? 'dark' : 'light'}>
                      <label>Keterangan</label>
                      <textarea value={teoriKetidakhadiranData.description} onChange={e => setTeoriKetidakhadiranData({ ...teoriKetidakhadiranData, description: e.target.value })} required />
                    </FormGroup>
                    <FormActions>
                      <SubmitButton type="submit" disabled={submitting}>
                        {submitting ? 'Mengirim...' : 'Kirim Laporan Ketidaksesuaian'}
                      </SubmitButton>
                    </FormActions>
                  </form>
                )}
              </>
            )}
            
            {/* Case 3: BK teacher ONLY */}
            {(isGuruBk && !isGuruBengkel && !isGuruTeori) && (
              <form onSubmit={e => { e.preventDefault(); handleBkSubmit(); }}>
                <FormGroup theme={darkMode ? 'dark' : 'light'}>
                  <label htmlFor="namaSiswa">Nama Siswa</label>
                  <input
                    type="text"
                    id="namaSiswa"
                    name="namaSiswa"
                    value={bkData.studentSearch}
                    onChange={e => {
                      const value = e.target.value;
                      setBkData({ ...bkData, studentSearch: value });
                      setShowBkStudentSuggestions(true);
                    }}
                    onFocus={() => setShowBkStudentSuggestions(true)}
                    placeholder="Masukkan nama siswa"
                  />
                  {showBkStudentSuggestions && bkData.studentSearch && (
                    <SuggestionsList theme={darkMode ? 'dark' : 'light'}>
                      {studentOptions.filter(opt =>
                        opt.name.toLowerCase().includes(bkData.studentSearch.toLowerCase())
                      ).map(opt => (
                        <StudentSuggestionItem
                          key={opt.id}
                          theme={darkMode ? 'dark' : 'light'}
                          onClick={() => {
                            addStudentToList(opt, 'bk');
                          }}
                        >
                          <span className="student-name">{opt.name}</span>{' '}
                          <span className="student-info">({opt.class})</span>
                        </StudentSuggestionItem>
                      ))}
                    </SuggestionsList>
                  )}
                  <SelectedStudentsContainer>
                    {bkData.selectedStudents.map((studentId, idx) => (
                      <SelectedStudentTag key={studentId}>
                        <span style={{ fontWeight: 500 }}>{getStudentNameById(studentId)}</span>
                        <button type="button" onClick={() => {
                          removeStudentFromList(studentId, 'bk');
                        }}>×</button>
                      </SelectedStudentTag>
                    ))}
                  </SelectedStudentsContainer>
                </FormGroup>
                <FormGroup theme={darkMode ? 'dark' : 'light'}>
                  <label>Jenis Ketidaksesuaian</label>
                  <input
                    type="text"
                    placeholder="Ketik & cari jenis ketidaksesuaian..."
                    value={bkData.jenisKetidaksesuaianSearch || ''}
                    onChange={e => {
                      const value = e.target.value;
                      setBkData({ 
                        ...bkData, 
                        jenisKetidaksesuaianSearch: value,
                        jenisKetidaksesuaian: value,
                        kts_id: '', // Reset selection when typing
                        total_hour: '',
                        total_hour_kersos: '',
                        appreciation_point: '',
                        additional_consequence: '',
                        section: ''
                      });
                      setShowBkJenisKetidaksesuaianSuggestions(true);
                    }}
                    onFocus={() => setShowBkJenisKetidaksesuaianSuggestions(true)}
                    required
                  />
                  {showBkJenisKetidaksesuaianSuggestions && bkData.jenisKetidaksesuaianSearch && (
                    <SuggestionsList theme={darkMode ? 'dark' : 'light'}>
                      {getFilteredKtsOptions(bkData.jenisKetidaksesuaianSearch).map((option, index) => (
                        <SuggestionItem
                          key={index}
                          theme={darkMode ? 'dark' : 'light'}
                          onClick={() => {
                            handlePelanggaranSelection(option, 'bk');
                            setShowBkJenisKetidaksesuaianSuggestions(false);
                          }}
                        >
                          {option.description}
                        </SuggestionItem>
                      ))}
                    </SuggestionsList>
                  )}
                </FormGroup>
                <FormGroup theme={darkMode ? 'dark' : 'light'}>
                  <label>Section</label>
                  <input 
                    type="text" 
                    value={bkData.section} 
                    readOnly
                    style={{ backgroundColor: darkMode ? '#2a2a2a' : '#f5f5f5', cursor: 'not-allowed' }}
                    placeholder="Otomatis terisi berdasarkan jenis ketidaksesuaian"
                  />
                </FormGroup>
                <FormGroup theme={darkMode ? 'dark' : 'light'}>
                  <label>Jumlah Jam Lembur</label>
                  <input 
                    type="number" 
                    step="0.1"
                    value={bkData.total_hour} 
                    
                  
                    placeholder="Otomatis terisi berdasarkan jenis ketidaksesuaian"
                  />
                </FormGroup>
                <FormGroup theme={darkMode ? 'dark' : 'light'}>
                  <label>Jumlah Jam Kersos</label>
                  <input
                    type="number"
                    step="0.1"
                    value={bkData.total_hour_kersos}
                   
                    placeholder="Otomatis terisi berdasarkan jenis ketidaksesuaian"
                  />
                </FormGroup>
                <FormGroup theme={darkMode ? 'dark' : 'light'}>
                  <label>Poin Sikap</label>
                  <input
                    type="number"
                    step="0.1"
                    value={bkData.appreciation_point}
                    
                    placeholder="Otomatis terisi berdasarkan jenis ketidaksesuaian"
                  />
                </FormGroup>
                <FormGroup theme={darkMode ? 'dark' : 'light'}>
                  <label>Konsekuensi Tambahan</label>
                  <textarea
                    value={bkData.additional_consequence}
                    readOnly
                    style={{ backgroundColor: darkMode ? '#2a2a2a' : '#f5f5f5', cursor: 'not-allowed' }}
                    placeholder="Otomatis terisi berdasarkan jenis ketidaksesuaian"
                  />
                </FormGroup>
                <FormGroup theme={darkMode ? 'dark' : 'light'}>
                  <label>Keterangan</label>
                  <textarea value={bkData.description} onChange={e => setBkData({ ...bkData, description: e.target.value })} required />
                </FormGroup>
                <FormActions>
                  <SubmitButton type="submit" disabled={submitting}>
                    {submitting ? 'Mengirim...' : 'Kirim Laporan Ketidaksesuaian'}
                  </SubmitButton>
                </FormActions>
              </form>
            )}
          </FormContent>
        </FormWrapper>
      </FormContainer>
    </PageContainer>
  );
}

const PageContainer = styled.div`
  padding: 1.5rem;
  max-width: 1280px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
  
  h1 {
    font-size: 1.75rem;
    font-weight: 600;
    color: var(--text-dark);
    margin-bottom: 0.5rem;
  }
  
  p {
    color: var(--text-light);
  }
`;

const FormContainer = styled.div`
  background-color: var(--background);
  border-radius: 16px;
  padding: 2rem;
  box-shadow: var(--box-shadow);
  transition: background-color 0.3s, box-shadow 0.3s;
  
  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
  }
`;

const FormWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const TypeToggleContainer = styled.div`
  margin-bottom: 1.5rem;
`;

const TypeToggleButtonGroup = styled.div`
  display: flex;
  border: 1px solid var(--border);
  border-radius: 8px;
  overflow: hidden;
  gap: 16px;
  padding: 8px;
  background-color: var(--background-light);

  @media (max-width: 576px) {
    gap: 8px;
    padding: 6px;
  }
`;

const TypeToggleButton = styled.button`
  flex: 1;
  padding: 0.75rem 1.5rem;
  text-align: center;
  background-color: ${props => props.active ? 'var(--primary)' : 'var(--background)'};
  color: ${props => props.active ? 'white' : 'var(--text-dark)'};
  border: none;
  border-radius: 6px;
  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  transition: all 0.2s;
  font-weight: 500;
  white-space: normal;
  line-height: 1.2;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 576px) {
    padding: 0.5rem;
    font-size: 0.85rem;
    min-height: 52px;
  }
  
  &:hover {
    background-color: ${props => {
      if (props.disabled) return props.active ? 'var(--primary)' : 'var(--background)';
      return props.active ? 'var(--primary)' : 'var(--background-light)';
    }};
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  position: relative;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: var(--text-dark);
  }
  
  input, select, textarea {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid var(--border);
    border-radius: 8px;
    font-size: 1rem;
    color: var(--text-dark);
    background-color: ${props => props.theme === 'dark' ? '#383838' : 'white'};
    
    &:focus {
      outline: none;
      border-color: var(--primary);
      box-shadow: 0 0 0 2px rgba(51, 84, 165, 0.1);
    }
  }
  
  textarea {
    min-height: 100px;
    resize: vertical;
  }
`;

const FormRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  
  ${FormGroup} {
    flex: 1;
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 2rem;
  gap: 1rem;
`;

const SubmitButton = styled.button`
  background-color: var(--primary);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s, opacity 0.2s;

  &:hover {
    background-color: var(--primary-light);
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
    opacity: 0.6;

    &:hover {
      background-color: #ccc;
    }
  }
`;

const FormHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid var(--border);
  
  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  
  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-dark);
    margin: 0;
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  color: var(--text-light);
  
  &:hover {
    background-color: var(--background-light);
  }
`;

const FormContent = styled.div`
  padding: 1.5rem;
  
  form {
    max-width: 800px;
    margin: 0 auto;
  }
  
  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
  }
`;

const HistorySection = styled.div`
  background-color: var(--background);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: var(--box-shadow);
  transition: background-color 0.3s, box-shadow 0.3s;
  margin-top: 2rem;
  
  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 1.5rem;
    color: var(--text-dark);
  }
`;

const RequestTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9375rem;
  
  th, td {
    padding: 1rem;
    text-align: left;
    border-bottom: 1px solid var(--border);
  }
  
  th {
    font-weight: 600;
    color: var(--text-dark);
    background-color: var(--background-light);
  }
  
  tr:last-child td {
    border-bottom: none;
  }
  
  @media (max-width: 768px) {
    display: block;
    overflow-x: auto;
    
    th, td {
      padding: 0.75rem;
    }
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 50px;
  font-size: 0.75rem;
  font-weight: 600;
  background-color: ${props => props.$status === 'active' ? '#e6f8f5' : '#ffe6e6'};
  color: ${props => props.$status === 'active' ? '#2ec27e' : '#c01c28'};
`;

const ValueText = styled.span`
  color: ${props => props.$negative ? 'var(--error)' : props.$positive ? 'var(--success)' : 'var(--text-dark)'};
  font-weight: ${props => (props.$negative || props.$positive) ? '600' : '400'};
`;

const KategoriTag = styled.span`
  background-color: ${props => props.$kategori === 'sikap' ? 'var(--warning-light)' : 'var(--info-light)'};
  color: ${props => props.$kategori === 'sikap' ? 'var(--warning-dark)' : 'var(--info-dark)'};
  padding: 0.25rem 0.75rem;
  border-radius: 50px;
  font-size: 0.75rem;
  font-weight: 600;
`;

const ActionButton = styled.button`
  background-color: transparent;
  color: var(--primary);
  border: 1px solid var(--primary);
  border-radius: 4px;
  padding: 0.35rem 0.75rem;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;
  white-space: normal;
  line-height: 1.2;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 576px) {
    padding: 0.5rem;
    font-size: 0.85rem;
    min-height: 52px;
  }
  
  &:hover {
    background-color: var(--primary);
    color: white;
  }
`;

const AddButton = styled.button`
  background-color: var(--primary);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.6rem 1rem;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  
  &:hover {
    background-color: var(--primary-light);
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const CancelButton = styled.button`
  background-color: transparent;
  color: var(--text-dark);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: var(--background-light);
  }
`;

const HistoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  
  h2 {
    margin: 0;
  }
  
  @media (max-width: 576px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const ActionContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ActionCard = styled.div`
  background-color: var(--background);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: var(--box-shadow);
  transition: all 0.3s;
  display: flex;
  gap: 1.5rem;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.08);
  }
  
  @media (max-width: 576px) {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    text-align: center;
    padding: 1.5rem 1rem;
  }
`;

const CardIconWrapper = styled.div`
  width: 60px;
  height: 60px;
  background-color: var(--background-light);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  @media (max-width: 576px) {
    width: 50px;
    height: 50px;
  }
`;

const CardIcon = styled.div`
  font-size: 2rem;
  
  @media (max-width: 576px) {
    font-size: 1.75rem;
  }
`;

const CardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const CardTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: var(--text-dark);
`;

const CardDescription = styled.p`
  color: var(--text-light);
  font-size: 0.9rem;
  margin-bottom: 1.25rem;
`;

const RiwayatButton = styled.button`
  background-color: transparent;
  color: var(--primary);
  border: 1px solid var(--primary);
  border-radius: 6px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  
  &:hover {
    background-color: var(--primary);
    color: white;
  }
`;

const ResetButton = styled.button`
  background-color: transparent;
  color: var(--text-dark);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: var(--background-light);
  }
`;

const SuggestionsList = styled.ul`
  position: absolute;
  top: calc(100% - 0rem);
  left: 0;
  right: 0;
  margin: 0;
  padding: 0;
  list-style: none;
  border: 1px solid var(--border);
  border-radius: 8px;
  background-color: ${props => props.theme === 'dark' ? '#383838' : 'white'};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
`;

const SuggestionItem = styled.li`
  padding: 0.75rem 1rem;
  cursor: pointer;
  color: ${props => props.theme === 'dark' ? '#fff' : '#333'};

  &:hover {
    background-color: ${props => props.theme === 'dark' ? '#4a4a4a' : '#f5f5f5'};
  }

  &:not(:last-child) {
    border-bottom: 1px solid var(--border);
  }
`;

const StudentSuggestionItem = styled(SuggestionItem)`
  padding: 0.5rem 1rem;
  
  .student-name {
    font-weight: 500;
    margin-bottom: 0.25rem;
  }
  
  .student-info {
    font-size: 0.875rem;
    color: ${props => props.theme === 'dark' ? '#aaa' : '#666'};
  }
`;

const SelectedStudentsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const SelectedStudentTag = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: var(--background-light);
  padding: 0.5rem;
  border-radius: 6px;
  font-size: 0.875rem;
  
  span {
    color: var(--text-dark);
  }
  
  button {
    background: none;
    border: none;
    color: var(--text-light);
    cursor: pointer;
    padding: 0;
    font-size: 1.25rem;
    line-height: 1;
    display: flex;
    align-items: center;
    
    &:hover {
      color: var(--text-dark);
    }
  }
`;

const StudentName = styled.span`
  font-weight: 500;
  margin-bottom: 0.25rem;
`;

const StudentDetails = styled.span`
  font-size: 0.875rem;
  color: ${props => props.theme === 'dark' ? '#aaa' : '#666'};
`;

export default LaporKTSPage;